import { useEffect, useMemo, useState } from 'react'
import { API_URL } from '../../config'
import { useTranslation } from 'react-i18next'
import { useInitialData } from '../../components/data/DataContext'
import { Link } from 'react-router-dom'
import { ArrowRight, Banknote, BarChart3, Globe2, Handshake, Users } from 'lucide-react'
import { languagePrefixes } from '../../i18n'

const OurDataMarket = ({ bgColor = 'bg-white' }) => {
  const { t, i18n } = useTranslation()
  const { generalInfo } = useInitialData()
  const currentLanguage = (i18n.language || 'en').split('-')[0]
  const languagePrefix = languagePrefixes[currentLanguage] || ''

  const [data, setData] = useState(generalInfo ?? { totalInvestors: 0, totalDeals: 0, totalDealsAmount: 0, uniqueCountries: 0 })
  const [loading, setLoading] = useState(!generalInfo)
  const [error, setError] = useState(null)

  const formatCount = v =>
    v == null ? '0' : (typeof v === 'string' ? v : (Number.isNaN(+v) ? '0' : Number(v).toLocaleString()))

  const formatSuffixFloorPlus = v => {
    if (v == null) return '0'
    const m = String(v).trim().match(/^(-?\d+(?:\.\d+)?)([a-zA-Z]+)$/)
    if (m) {
      const n = Math.floor(Number(m[1])); if (Number.isNaN(n)) return String(v)
      return `$${n}${m[2]}+`
    }
    const n = Math.floor(Number(v)); return Number.isNaN(n) ? String(v) : n.toLocaleString()
  }

  const formatCompactFloor = v => {
    if (v == null) return '0'
    const n = Number(v); if (Number.isNaN(n)) return String(v)
    const abs = Math.abs(n), sign = n < 0 ? '-' : ''
    for (const u of [{ v: 1e12, s: 't' }, { v: 1e9, s: 'b' }, { v: 1e6, s: 'm' }, { v: 1e3, s: 'k' }]) {
      if (abs >= u.v) return `${sign}${Math.floor(abs / u.v)}${u.s}`
    }
    return `${n}`
  }

  useEffect(() => {
    if (generalInfo) return setLoading(false)
    fetch(`${API_URL}/general-info`)
      .then(r => r.json())
      .then(res => setData({
        totalInvestors: res.totalInvestors ?? 0,
        totalDeals: res.totalDeals ?? 0,
        totalDealsAmount: res.totalDealsAmount ?? 0,
        uniqueCountries: res.uniqueCountries ?? 0,
      }))
      .catch(e => { console.error(e); if (e.message !== 'Failed to fetch') setError(e.message) })
      .finally(() => setLoading(false))
  }, [generalInfo])

  const stats = useMemo(() => ([
    {
      Icon: Users,
      value: loading ? '…' : `${formatCompactFloor(data.totalInvestors)}+`,
      labelTop: t('pageHome.content.t10'),
      labelBottom: t('pageHome.content.t121', { count: formatCount(data.uniqueCountries) })
    },
    {
      Icon: Handshake,
      value: loading ? '…' : `${formatCompactFloor(data.totalDeals)}+`,
      labelTop: t('pageHome.content.t11'),
      labelBottom: t('pageHome.content.t122')
    },
    {
      Icon: Banknote,
      value: loading ? '…' : formatSuffixFloorPlus(data.totalDealsAmount),
      labelTop: t('pageHome.content.t12'),
      labelBottom: t('pageHome.content.t123')
    },
  ]), [loading, data, t])

  const createPath = (path) => `${languagePrefix}/${path.replace(/^\//, '')}`

  const CTAButton = ({ text, href, variant = 'primary', className = '' }) => (
    <Link
      to={createPath(href)}
      className={`inline-flex h-9 w-fit max-w-full items-center justify-center gap-2 rounded-full px-4 text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-stone-400/70 ${variant === 'primary'
        ? 'bg-stone-900 text-white shadow hover:bg-stone-900/90'
        : 'border border-stone-200 bg-white text-stone-900 hover:border-stone-300 hover:bg-stone-50'} ${className}`}
    >
      <span>{text}</span>
      <ArrowRight className="h-4 w-4" />
    </Link>
  )

  return (
    <section className={`py-20 sm:py-24 ${bgColor}`}>
      <div className="mx-auto max-w-screen-xl px-6 sm:px-12 lg:px-16">
        <div className="rounded-lg border border-stone-200 bg-white/90 p-5 shadow-sm backdrop-blur md:p-6 lg:grid lg:grid-cols-12 lg:gap-8 lg:p-8">
          <div className="lg:col-span-5">
            <div className="mb-4 inline-flex items-center gap-2 rounded-full border border-blue-100 bg-blue-50 px-3 py-1 text-xs font-medium text-blue-700">
              <BarChart3 className="h-3.5 w-3.5" />
              {t('pageHome.content.t61')}
            </div>

            <div className="grid gap-3 sm:grid-cols-2">
              {stats.map((s, i) => (
                <div
                  key={i}
                  className={`${i === 2 ? 'sm:col-span-2' : ''} rounded-lg border border-stone-200 bg-stone-50/70 p-4`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-white text-stone-700 shadow-sm ring-1 ring-stone-200">
                      <s.Icon aria-hidden="true" className="h-5 w-5" />
                    </div>
                    {i === 0 && (
                      <div className="inline-flex items-center gap-1 rounded-full bg-white px-2 py-1 text-[11px] font-medium text-stone-500 ring-1 ring-stone-200">
                        <Globe2 className="h-3 w-3" />
                        {formatCount(data.uniqueCountries)}
                      </div>
                    )}
                  </div>
                  <div className="mt-4 text-3xl font-semibold tracking-tight text-stone-950 sm:text-4xl">
                    {s.value}
                  </div>
                  <div className="mt-2 text-sm font-medium text-stone-700">{s.labelTop}</div>
                  {s.labelBottom && <div className="text-sm text-stone-500">{s.labelBottom}</div>}
                </div>
              ))}
            </div>

            <div className="mt-4">
              <CTAButton text={t('pageHome.content.t65')} href="find-investors" variant="secondary" />
            </div>
          </div>

          <div className="mt-10 flex flex-col justify-center lg:col-span-7 lg:mt-0">
            <h2 className="text-3xl font-bold leading-tight tracking-tight text-stone-950 sm:text-4xl">{t('pageHome.content.t62')}</h2>
            <div className="mt-5 grid gap-4 text-base leading-7 text-stone-600">
              <p>{t('pageHome.content.t63')}</p>
              <p>{t('pageHome.content.t64')}</p>
            </div>
            <div className="mt-6">
              <CTAButton text={t('pageHome.content.t91')} href="pricing" />
            </div>
          </div>
        </div>
      </div>
      {error && error !== 'Failed to fetch' && <p className="text-red-500 text-center mt-4">Error: {error}</p>}
    </section>
  )
}

export default OurDataMarket
